import React, { useState, useEffect, useContext, useRef } from "react"
import PropTypes from "prop-types"
import {
  notificationPopupTitle,
  notificationPopupModal,
  notificationPopupWrapper,
  notificationPopupHeader,
  notificationPopupContent,
  notificationPopupButton,
} from "./notification-popup.module.scss"
import ContentTransformer from "../Transformers/content-transformer"
import { useBuildData } from "../../hooks/use-build-data"
import EditButton from "gatsby-theme-blank/src/components/EditButton/edit-button"
import {
  Title,
  NotificationPopupWrapper,
  NotificationPopupButton,
  NotificationPopupContent,
} from "./notification-popup-styled"
import { NotificationContext } from "gatsby-theme-blank/src/components/notification-context"

const NotificationPopup = ({ popupContent, location }) => {
  const { isOpen, setIsOpen } = useContext(NotificationContext)
  const [isRegistered, setIsRegistered] = useState(false)
  const publicKey = useBuildData().site_settings.webpush_public_key
  const autoOpening =
    useBuildData().site_settings.notification_popup_auto_opening
  const enabled = useBuildData().site_settings.webpush_enabled
  const [activeIndex, setActiveIndex] = useState(-1)
  const activeIndexRef = useRef(activeIndex)
  const setActiveIndexValue = (newIndex) => {
    activeIndexRef.current = newIndex
    setActiveIndex(newIndex)
  }
  const [focusableElements, setFocusableElements] = useState([])
  const modalRef = useRef(null)
  const [isMaintenance, setIsMaintenance] = useState(false)

  const handleEscape = (event) => {
    if (event.key === "Escape") setIsOpen(false)
  }

  const handleTab = (event) => {
    let actualFocusableElements = focusableElements
    if (modalRef && !focusableElements?.length) {
      actualFocusableElements = modalRef.current?.querySelectorAll(
        "a, button, textarea, input, select"
      )
      setFocusableElements(actualFocusableElements)
    }

    const total = actualFocusableElements?.length
    if (!total) return
    let localActiveIndex = activeIndexRef.current

    let isTACOpen = false
    if (typeof document !== `undefined`) {
      const body = document.body
      isTACOpen = body.className === "tarteaucitron-modal-open"
    }
    if (!isTACOpen) {
      // Tab navigation
      if (!event.shiftKey) {
        localActiveIndex =
          localActiveIndex + 1 === total
            ? (localActiveIndex = 0)
            : (localActiveIndex += 1)

        actualFocusableElements[localActiveIndex].focus()
        setActiveIndexValue(localActiveIndex)
        return event.preventDefault()
      }

      // Tab navigation avec shift
      if (event.shiftKey) {
        localActiveIndex =
          localActiveIndex - 1 < 0
            ? (localActiveIndex = total - 1)
            : (localActiveIndex -= 1)

        actualFocusableElements[localActiveIndex].focus()
        setActiveIndexValue(localActiveIndex)
        return event.preventDefault()
      }
    }
  }

  const keyListenersMap = new Map([
    [27, handleEscape],
    [9, handleTab],
  ])

  const handleKeydown = (event) => {
    const listener = keyListenersMap.get(event.keyCode)
    return listener && listener(event)
  }

  // Requis à cause du re-render qui duplique les listeners
  const handleKeyDownRef = useRef(handleKeydown)

  useEffect(() => {
    if (!navigator.serviceWorker) return
    const manuallyClosed = sessionStorage.notificationPopupManuallyClosed
    navigator.serviceWorker.ready
      .then((serviceWorkerRegistration) =>
        serviceWorkerRegistration.pushManager.getSubscription()
      )
      .then((subscription) => {
        if (!subscription) {
          setIsRegistered(false)
          if (enabled && !manuallyClosed && autoOpening)
            setTimeout(() => {
              setIsOpen(true)
            }, 10000)
        } else {
          setIsRegistered(true)
        }
      })
  }, [])

  // On attache / détache les listeners lors de l'ouverture / fermeture
  useEffect(() => {
    setActiveIndexValue(-1)
    if (!isOpen) {
      document.removeEventListener("keydown", handleKeyDownRef.current)
    } else {
      document.removeEventListener("keydown", handleKeyDownRef.current)
      document.addEventListener("keydown", handleKeyDownRef.current)
    }
  }, [isOpen])

  const urlBase64ToUint8Array = function (base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4)
    const base64 = (base64String + padding)
      .replace(/-/g, "+")
      .replace(/_/g, "/")

    const rawData = window.atob(base64)
    const outputArray = new Uint8Array(rawData.length)

    for (let index = 0; index < rawData.length; ++index) {
      outputArray[index] = rawData.charCodeAt(index)
    }
    return outputArray
  }

  useEffect(() => {
    const fetchMaintenance = async () => {
      const resp = await fetch(
        process.env.GATSBYSIDE_DRUPAL_URL + `/api/get/maintenance/state`
      )
      const maintenanceMessage = await resp.text()
      setIsMaintenance(maintenanceMessage !== "0")
    }

    fetchMaintenance()
  }, [])

  const checkNotificationPermission = function () {
    return new Promise((resolve, reject) => {
      if (Notification.permission === "denied") {
        return reject(new Error("Push messages are blocked."))
      }

      if (Notification.permission === "granted") {
        return resolve()
      }

      if (Notification.permission === "default") {
        return Notification.requestPermission().then((result) => {
          if (result !== "granted") {
            reject(new Error("Bad permission result"))
          } else {
            resolve()
          }
        })
      }

      return reject(new Error("Unknown permission"))
    })
  }

  const checkBrowserCompatibility = function () {
    // Todo : Informer l'utilisateur en cas d'échec
    if (!("serviceWorker" in navigator)) {
      // console.warn("Service workers are not supported by this browser")
      return false
    }

    if (!("PushManager" in window)) {
      // console.warn("Push notifications are not supported by this browser")
      return false
    }

    if (!("showNotification" in ServiceWorkerRegistration.prototype)) {
      // console.warn("Notifications are not supported by this browser")
      return false
    }

    return true
  }

  const subscribe = function () {
    if (!checkBrowserCompatibility()) return
    return checkNotificationPermission()
      .then(() => navigator.serviceWorker.ready)
      .then((serviceWorkerRegistration) =>
        serviceWorkerRegistration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        })
      )
      .then((subscription) => {
        const key = subscription.getKey("p256dh")
        const token = subscription.getKey("auth")
        const contentEncoding = (PushManager.supportedContentEncodings || [
          "aesgcm",
        ])[0]
        const url = process.env.GATSBYSIDE_DRUPAL_URL + "/api/webpush/subscribe"

        const options = {
          method: "POST",
          cache: "no-cache",
          credentials: "omit",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            endpoint: subscription.endpoint,
            publicKey: key
              ? btoa(String.fromCharCode.apply(null, new Uint8Array(key)))
              : null,
            authToken: token
              ? btoa(String.fromCharCode.apply(null, new Uint8Array(token)))
              : null,
            contentEncoding,
          }),
        }
        fetch(url, options).then((response) => {
          response.text().then(() => {
            // On a enregistré l'utilisateur dans Drupal
            // donc on ferme la popup
            setIsRegistered(true)
            setIsOpen(false)
          })
          return response
        })
      })
      .then((subscription) => {
        setIsOpen(false)
      })
      .catch((error) => {
        if (Notification.permission === "denied") {
          // console.warn("Notifications are denied by the user.")
        } else {
          // console.error("Impossible to subscribe to push notifications", error)
        }
      })
  }

  const unsubscribe = function () {
    if (!checkBrowserCompatibility()) return
    return checkNotificationPermission()
      .then(() => navigator.serviceWorker.ready)
      .then((serviceWorkerRegistration) =>
        serviceWorkerRegistration.pushManager.getSubscription()
      )
      .then((subscription) => subscription.unsubscribe())
      .then(() => {
        setIsOpen(false)
        setIsRegistered(false)
      })
  }

  return (
    <div ref={modalRef}>
      {isOpen && (
        <div
          className={notificationPopupModal}
          role="dialog"
          aria-modal="true"
          onClick={(event) => {
            // On empêche que cliquer sur l'enfant ferme aussi la popup
            if (event.target !== event.currentTarget) return
            setIsOpen(false)
          }}
          aria-labelledby="notificationTitle"
        >
          <NotificationPopupWrapper className={notificationPopupWrapper}>
            <div className={notificationPopupHeader}>
              <Title
                className={notificationPopupTitle}
                id={"notificationTitle"}
              >
                S’abonner aux notifications de {location.hostname}
              </Title>
              <NotificationPopupButton
                className={notificationPopupButton}
                onClick={() => {
                  setIsOpen(false)
                  sessionStorage.setItem(
                    "notificationPopupManuallyClosed",
                    true
                  )
                }}
                aria-label="Fermer la fenêtre"
              >
                <span aria-hidden={true}>&times;</span>
              </NotificationPopupButton>
            </div>
            <NotificationPopupContent className={notificationPopupContent}>
              <EditButton link={"/admin/webpush/popup"} />

              <ContentTransformer content={popupContent}></ContentTransformer>
              {isMaintenance ? (
                <b>
                  L&apos;inscription et la désinscription aux notifications sont
                  momentanéments indisponibles.
                </b>
              ) : isRegistered ? (
                <button
                  className={"btn"}
                  onClick={() => unsubscribe()}
                  tabIndex="0"
                >
                  Désactiver les notifications
                </button>
              ) : (
                <button
                  className={"btn"}
                  onClick={() => subscribe()}
                  tabIndex="0"
                >
                  Activer les notifications
                </button>
              )}
            </NotificationPopupContent>
          </NotificationPopupWrapper>
        </div>
      )}
    </div>
  )
}

//-----------------------Prop-Types---------------------------//

NotificationPopup.propTypes = {
  popupContent: PropTypes.string,
  location: PropTypes.object,
}

//-----------------------Prop-Types---------------------------//

export default NotificationPopup
